import { ActivatedRoute, Router } from '@angular/router'
import { Component, OnInit } from '@angular/core'
import { MessageService } from 'primeng/api'

import { ApiWalletConfigService } from '../../data/services/api/api-wallet-config.service'
import { WalletConfigService } from '../../data/services/view/wallet-config.service'

@Component({
  selector: 'app-wallet-config-detail',
  templateUrl: './wallet-config-detail.component.html',
  styleUrls: ['./wallet-config-detail.component.scss'],
  providers: [MessageService]
})
export class WalletConfigDetailComponent implements OnInit {
  report: any = null
  loading = true
  downloading = false // Nuevo estado para descarga
  reportId: string

  constructor(
    private apiWalletConfigService: ApiWalletConfigService,
    public walletConfigService: WalletConfigService,
    private messageService: MessageService,
    private route: ActivatedRoute,
    private router: Router,
  ) {
    this.reportId = this.route.snapshot.paramMap.get('id') || ''
  }

  ngOnInit(): void {
    if (this.reportId) {
      this.loadReportDetails()
    } else {
      this.router.navigate(['/wallet-config'])
    }
  }

  loadReportDetails(): void {
    this.loading = true
    
    this.apiWalletConfigService.getById(this.reportId).subscribe({
      next: (response) => {
        this.loading = false
        if (!response.error) {
          this.report = response.data
          this.walletConfigService.setSelectedReport(this.report)
          
          // Debugging temporal - mostrar la estructura real de datos
          console.log('Report data structure:', this.report)
          console.log('Available fields:', Object.keys(this.report || {}))
        } else {
          this.messageService.add({
            severity: 'error',
            summary: 'Error',
            detail: response.msg || 'No se pudo cargar el reporte'
          })
          this.router.navigate(['/wallet-config'])
        }
      },
      error: () => {
        this.loading = false
        this.messageService.add({
          severity: 'error',
          summary: 'Error',
          detail: 'Error al conectar con el servidor'
        })
        this.router.navigate(['/wallet-config'])
      }
    })
  }

  downloadReport(): void {
    // Validaciones previas según documentación
    if (!this.report) {
      this.messageService.add({
        severity: 'error',
        summary: 'Error',
        detail: 'No hay reporte disponible'
      })
      return
    }

    if (this.report.status !== 'success') {
      this.messageService.add({
        severity: 'warning',
        summary: 'Archivo no disponible',
        detail: 'Solo los reportes exitosos tienen archivos disponibles'
      })
      return
    }

    if (!this.report.fileUrl) {
      this.messageService.add({
        severity: 'warning',
        summary: 'Sin archivo',
        detail: 'Este reporte no tiene archivo asociado'
      })
      return
    }

    // Iniciar descarga
    this.downloading = true

    this.apiWalletConfigService.downloadReportFile(this.reportId).subscribe({
      next: (blob: Blob) => {
        this.downloading = false
        
        // Generar nombre de archivo con fecha
        const timestamp = new Date().toISOString().slice(0, 10) // YYYY-MM-DD
        const defaultFilename = `reporte-cartera-${timestamp}.csv`
        
        // Triggear descarga
        this.triggerFileDownload(blob, defaultFilename)
        
        this.messageService.add({
          severity: 'success',
          summary: 'Descarga exitosa',
          detail: 'El archivo se ha descargado correctamente'
        })
      },
      error: (error) => {
        this.downloading = false
        this.handleDownloadError(error)
      }
    })
  }

  private triggerFileDownload(blob: Blob, filename: string): void {
    // Crear URL temporal para el blob
    const url = window.URL.createObjectURL(blob)
    
    // Crear elemento link temporal
    const link = document.createElement('a')
    link.href = url
    link.download = filename
    link.style.display = 'none'
    
    // Agregar al DOM, hacer click y remover
    document.body.appendChild(link)
    link.click()
    document.body.removeChild(link)
    
    // Cleanup URL temporal
    window.URL.revokeObjectURL(url)
  }

  private handleDownloadError(error: any): void {
    console.error('Download error:', error)
    
    // Manejo específico de errores según documentación
    if (error.status === 400) {
      this.messageService.add({
        severity: 'warning',
        summary: 'Archivo no encontrado',
        detail: 'El reporte no tiene archivo asociado'
      })
    } else if (error.status === 403) {
      this.messageService.add({
        severity: 'error',
        summary: 'Sin permisos',
        detail: 'No tienes permisos para descargar este archivo'
      })
    } else if (error.status === 401) {
      this.messageService.add({
        severity: 'error',
        summary: 'Sesión expirada',
        detail: 'Tu sesión ha expirado. Por favor inicia sesión nuevamente'
      })
    } else {
      this.messageService.add({
        severity: 'error',
        summary: 'Error de descarga',
        detail: 'Error al descargar el archivo. Intenta nuevamente'
      })
    }
  }

  goBack(): void {
    this.router.navigate(['/wallet-config'])
  }

  getStatusSeverity(status: string): string {
    const statusInfo = this.walletConfigService.formatReportStatus(status)
    return statusInfo.severity
  }

  getStatusLabel(status: string): string {
    const statusInfo = this.walletConfigService.formatReportStatus(status)
    return statusInfo.label
  }

  formatFileSize(bytes: number): string {
    return this.walletConfigService.formatFileSize(bytes)
  }

  formatProcessingTime(): string {
    if (this.report?.startTime && this.report?.endTime) {
      return this.walletConfigService.calculateProcessingDuration(
        this.report.startTime, 
        this.report.endTime
      )
    }
    return 'N/A'
  }
}